import * as yup from 'yup'
import { STAY_DRIVE, HOTEL, FLIGHT } from './TripTypes'

const LOCATION_REQUIRED = 'Please enter a city or airport'
const INVALID_DATE = 'Please select valid dates'
const DISALLOW_INFANT_INTL_FLIGHT =
  'Sorry, Priceline does not offer lap infant seating on international flights'
const INFANT_CAPACITY_LIMIT = 'Only one infant per adult is allowed'
const CHILD_AGE_REQUIRED =
  "Please enter children's age to get the best price for your group"
const TRAVELERS_PER_ROOM = 4
const ROOM_CAPACITY_ERROR =
  'Rooms accommodate up to 4 people. You may need additional rooms for your group'

const intlStartLocation = startLocationObject =>
  startLocationObject &&
  typeof startLocationObject.countryCode === 'string' &&
  startLocationObject.countryCode.toUpperCase() !== 'US'

const intlEndLocation = endLocationObject =>
  endLocationObject &&
  typeof endLocationObject.countryCode === 'string' &&
  endLocationObject.countryCode.toUpperCase() !== 'US'

export const isInfantFound = childrenAges =>
  Array.isArray(childrenAges) && childrenAges.some(age => Number(age) === 0)

const isStartLocationWithInfant = (startLocationObject, childrenAges) =>
  intlStartLocation(startLocationObject) && isInfantFound(childrenAges)
export const isEndLocationWithInfant = (endLocationObject, childrenAges) => {
  return intlEndLocation(endLocationObject) && isInfantFound(childrenAges)
}

const isChildrenAgeDataValid = childrenAges =>
  Array.isArray(childrenAges) && childrenAges.length >= 0
export const numOfValidAges = childrenAges =>
  Array.isArray(childrenAges) && childrenAges.filter(age => age >= 0)

export const isInfantOnInternationalTravel = (
  startLocation,
  endLocation,
  childrenAges
) => {
  if (
    isStartLocationWithInfant(startLocation, childrenAges) ||
    isEndLocationWithInfant(endLocation, childrenAges)
  ) {
    return DISALLOW_INFANT_INTL_FLIGHT
  }
  return ''
}

function handleIsInfantOnInternationalTravel() {
  const { startLocation, endLocation, childrenAges } = this.parent
  if (
    isStartLocationWithInfant(startLocation, childrenAges) ||
    isEndLocationWithInfant(endLocation, childrenAges)
  ) {
    return false
  }
  return true
}

export const isRoomCompacityReached = (
  adultCount,
  childrenCount,
  tripType,
  roomCount
) => {
  const travelerCount = adultCount + childrenCount
  const allowedTravelerCount = roomCount * TRAVELERS_PER_ROOM
  if (allowedTravelerCount < travelerCount && tripType.includes(HOTEL)) {
    return ROOM_CAPACITY_ERROR
  }
  return ''
}

function handleIsRoomCompacityReached() {
  const { adultCount, childrenCount, tripType, roomCount } = this.parent
  const travelerCount = adultCount + childrenCount
  const allowedTravelerCount = roomCount * TRAVELERS_PER_ROOM
  if (allowedTravelerCount < travelerCount && tripType.includes(HOTEL)) {
    return false
  }
  return true
}

function handleIsChildrenAgeProvided() {
  const { childrenCount, childrenAges } = this.parent
  if (
    childrenCount > 0 &&
    isChildrenAgeDataValid(childrenAges) &&
    childrenCount !== numOfValidAges(childrenAges).length
  ) {
    return false
  }
  return true
}

export const isChildrenAgeProvided = (childrenCount, childrenAges) => {
  if (
    childrenCount > 0 &&
    isChildrenAgeDataValid(childrenAges) &&
    childrenCount !== numOfValidAges(childrenAges).length
  ) {
    return CHILD_AGE_REQUIRED
  }
  return ''
}

export const isInfantLimitReachedPackages = (
  adultCount,
  childrenAges,
  tripType
) => {
  const lapInfants = childrenAges.filter(age => age === 0)
  const infantCount = lapInfants.length
  if (tripType.includes(FLIGHT) && infantCount > adultCount) {
    return INFANT_CAPACITY_LIMIT
  }
  return ''
}

export const searchFormSchema = yup.object().shape({
  tripType: yup.string(),
  partialStay: yup.bool(),
  startLocation: yup
    .object()
    .nullable()
    .when('tripType', {
      is: STAY_DRIVE,
      then: yup.object().nullable(),
      otherwise: yup
        .object()
        .nullable()
        .required(LOCATION_REQUIRED)
    }),
  endLocation: yup
    .object()
    .nullable()
    .required(LOCATION_REQUIRED),
  rcLocation: yup
    .object()
    .nullable()
    .when('pickupCarSomewhere', {
      is: true,
      then: yup
        .object()
        .nullable()
        .required(LOCATION_REQUIRED),
      otherwise: yup.object().nullable()
    }),
  startDate: yup.string().required(INVALID_DATE),
  endDate: yup.string().required(INVALID_DATE),
  hotelStartDate: yup.string().when('partialStay', {
    is: true,
    then: yup.string().required(INVALID_DATE),
    otherwise: yup.string()
  }),
  hotelEndDate: yup.string().when('partialStay', {
    is: true,
    then: yup.string().required(INVALID_DATE),
    otherwise: yup.string()
  }),
  rcPickupDate: yup.string().when('pickupCarSomewhere', {
    is: true,
    then: yup
      .string()
      .nullable()
      .required(INVALID_DATE),
    otherwise: yup.string()
  }),
  rcDropoffDate: yup.string().when('pickupCarSomewhere', {
    is: true,
    then: yup
      .string()
      .nullable()
      .required(INVALID_DATE),
    otherwise: yup.string()
  }),
  isInfantOnInternationalTravel: yup
    .string()
    .test(
      'isInfantOnInternationalTravel',
      DISALLOW_INFANT_INTL_FLIGHT,
      handleIsInfantOnInternationalTravel
    ),
  isChildrenAgeProvided: yup
    .string()
    .test(
      'isChildrenAgeProvided',
      CHILD_AGE_REQUIRED,
      handleIsChildrenAgeProvided
    ),
  isRoomCompacityReached: yup
    .string()
    .test(
      'isRoomCompacityReached',
      ROOM_CAPACITY_ERROR,
      handleIsRoomCompacityReached
    )
})
